import {
  editorUtils,
  getValueByPath,
  setValueByPath,
  useEditorStore,
  useFlowBuilder2Context,
} from '@diana/core';
import produce from 'immer';
import { cloneDeep, isEqual } from 'lodash';
import { Fragment, ReactNode, useCallback, useRef } from 'react';
import { useFlowBuilder2PageStore } from '../../../Store/useFlowBuilder2PageStore';
import { ILayoutComponentEditorProps } from './LayoutComponentEditor/LayoutComponentEditor.types';

type TConfigureComponentProps = ILayoutComponentEditorProps;

export const ConfigureComponentProps = (props: {
  children: (configureComponentProps: TConfigureComponentProps) => ReactNode;
}) => {
  const { children } = props;

  const indexRef = useRef<number>(-1);

  const activeComponent = useFlowBuilder2PageStore((s) => s.activeComponent, isEqual);

  const getLayoutById = useEditorStore((s) => s.getLayoutById);
  const flowId = useEditorStore((s) => s.flow?.id ?? null);

  const value = useEditorStore((s) => {
    if (activeComponent) {
      const { layoutId, componentId } = activeComponent;

      const layout = s.getLayoutById(layoutId);

      if (layout) {
        if (layout.components[indexRef.current]?.id === componentId) {
          return layout.components[indexRef.current];
        }

        indexRef.current = layout.components.findIndex((c) => c.id === componentId);
        if (layout.components[indexRef.current]) {
          return layout.components[indexRef.current];
        }
      }
    }
    return null;
  }, isEqual);

  const data = useEditorStore((s) => {
    if (value && s.flow) {
      const json = editorUtils.getDefaultDataAsObjectFromFlowConfig(s.flow);
      const path = value.dataPath;
      if (path && path.startsWith('$')) {
        return getValueByPath({ json, path });
      }
    }
    return null;
  }, isEqual);

  const { updateAndSaveLayoutComponentByIndex, updateAndSaveDefaultData } =
    useFlowBuilder2Context();

  const onChange = useCallback<ILayoutComponentEditorProps['onChange']>(
    (component) => {
      if (activeComponent) {
        const layout = getLayoutById(activeComponent.layoutId);
        const currentIndex = layout?.components?.findIndex((c) => c.id === component.id) ?? -1;
        if (currentIndex >= 0) {
          updateAndSaveLayoutComponentByIndex(activeComponent.layoutId, currentIndex, component);
        }
      }
    },
    [getLayoutById, activeComponent, updateAndSaveLayoutComponentByIndex],
  );

  const onDataChange = useCallback<ILayoutComponentEditorProps['onDataChange']>(
    (newData) => {
      if (value?.dataPath.startsWith('$')) {
        updateAndSaveDefaultData((defaultData) => {
          return produce(defaultData, (draft) => {
            setValueByPath({
              json: draft,
              path: value.dataPath,
              value: typeof newData === 'function' ? newData(cloneDeep(data)) : newData,
              clone: false,
            });
          });
        });
      }
    },
    [data, updateAndSaveDefaultData, value?.dataPath],
  );

  if (activeComponent && value)
    return (
      <Fragment>
        {children({
          flowId,
          layoutId: activeComponent.layoutId,
          componentIndex: indexRef.current,
          value,
          onChange,
          data,
          onDataChange,
        })}
      </Fragment>
    );

  return <Fragment />;
};
